import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import styled, { css, keyframes } from 'styled-components'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Card } from 'react-bootstrap'
import RouteContainer from '../../../common/styles/RouteContainer'
import RowSpacer from '../../../common/styles/RowSpacer'
import CardBodyContentContainer from '../../../common/styles/CardBodyContentContainer'
import NoContentForFullScreen from '../../../common/NoContentForFullScreen'
import LoadingBar from '../../../common/LoadingBar'
import useBookPublisher from './useBookPublisher'
import useHtmlTitle from '../../../common/useHtmlTitle'
import booksitoutIcon from '../../../config/BooksitoutIcon'
import BookIsbnCard, { BookIsbnCardGrid } from '../common/BookIsbnCard'
import useGridColumns from '../../../common/hooks/useGridColumns'
import { BookPublisherSummary } from './BookPublisherResponse'

const COUNTRY_LABEL: Record<string, string> = {
	US: '🇺🇸 미국',
	UK: '🇬🇧 영국',
	KO: '🇰🇷 대한민국',
	JP: '🇯🇵 일본',
	CN: '🇨🇳 중국',
	FR: '🇫🇷 프랑스',
	ES: '🇪🇸 스페인',
	DE: '🇩🇪 독일',
	IT: '🇮🇹 이탈리아',
	RU: '🇷🇺 러시아',
	SA: '🇸🇦 사우디아라비아',
	CA: '🇨🇦 캐나다',
	TW: '🇹🇼 대만',
	AU: '🇦🇺 호주',
	NL: '🇳🇱 네덜란드',
	SE: '🇸🇪 스웨덴',
	PL: '🇵🇱 폴란드',
	IN: '🇮🇳 인도',
	BR: '🇧🇷 브라질',
	RO: '🇷🇴 루마니아',
	UA: '🇺🇦 우크라이나',
}

const PublisherLogo = ({ src, size = 100 }: { src: string | null; size?: number }) => {
	const [failed, setFailed] = useState(false)
	if (!src || failed) {
		return (
			<LogoIconWrapper size={size}>
				<booksitoutIcon.building size={size * 0.4} />
			</LogoIconWrapper>
		)
	}
	return <LogoImage src={src} alt="" size={size} onError={() => setFailed(true)} />
}

const RelatedPublisherChip = ({ publisher }: { publisher: BookPublisherSummary }) => {
	const content = (
		<>
			<PublisherLogo src={publisher.logoImageUrl} size={28} />
			<span>{publisher.name}</span>
		</>
	)
	if (publisher.urlName == null) return <RelatedPublisherChipStatic>{content}</RelatedPublisherChipStatic>
	return <RelatedPublisherChipLink to={`/book/publisher/${publisher.urlName}`}>{content}</RelatedPublisherChipLink>
}

const SkeletonRow = ({ columns }: { columns: number }) => (
	<BookIsbnCardGrid>
		{Array.from({ length: columns }).map((_, i) => (
			<div key={i}>
				<Card className="h-100">
					<CardBodyContentContainer>
						<SkeletonBookCover />
						<RowSpacer size={10} />
						<LoadingBar size={3} />
						<RowSpacer size={5} />
						<LoadingBar size={2} />
					</CardBodyContentContainer>
				</Card>
			</div>
		))}
	</BookIsbnCardGrid>
)

const BookPublisherRoute = () => {
	const { urlName } = useParams()
	// 모회사/하위 출판사로 이동하면 같은 컴포넌트가 재사용되므로 urlName 이 바뀔 때 상태를 초기화
	return <BookPublisherContent key={urlName} urlName={urlName ?? null} />
}

const BookPublisherContent = ({ urlName }: { urlName: string | null }) => {
	const [data, books, isLoading, isError, paging] = useBookPublisher(urlName)
	const [gridRef, columns] = useGridColumns()

	useHtmlTitle(data?.publisher.name ?? '출판사')

	if (isLoading) return (
		<RouteContainer>
			<RowSpacer size={40} />
			<SkeletonHeader>
				<SkeletonCircle />
				<RowSpacer size={15} />
				<SkeletonBar width={200} height={32} />
				<RowSpacer size={10} />
				<SkeletonBar width={100} height={16} />
				<RowSpacer size={8} />
				<SkeletonBar width={120} height={16} />
			</SkeletonHeader>
			<RowSpacer size={30} />
			<SectionHeader>
				<SkeletonBar width={60} height={20} />
				<SkeletonBar width={40} height={16} />
			</SectionHeader>
			<RowSpacer size={10} />
			<BookIsbnCardGrid ref={gridRef}>
				{Array.from({ length: columns || 7 }).map((_, i) => (
					<div key={i}>
						<Card className="h-100">
							<CardBodyContentContainer>
								<SkeletonBookCover />
								<RowSpacer size={10} />
								<SkeletonBar width={100} height={14} style={{ margin: '0 auto' }} />
								<RowSpacer size={5} />
								<SkeletonBar width={60} height={12} style={{ margin: '0 auto' }} />
							</CardBodyContentContainer>
						</Card>
					</div>
				))}
			</BookIsbnCardGrid>
			<RowSpacer size={40} />
		</RouteContainer>
	)
	if (isError || data == null) return <NoContentForFullScreen message={'출판사 정보를 찾을 수 없어요'} />

	const { publisher, parentPublisher, childPublishers } = data
	const totalBookCount = data.books.totalElements
	const visibleCount = columns > 0 ? Math.floor(books.length / columns) * columns : books.length
	const visibleBooks = paging.hasMore && visibleCount > 0
		? books.slice(0, visibleCount)
		: books

	return (
		<RouteContainer>
			<RowSpacer size={40} />

			<PublisherHeader
				as={publisher.homePageUrl ? 'a' : 'div'}
				href={publisher.homePageUrl ?? undefined}
				target={publisher.homePageUrl ? '_blank' : undefined}
				rel={publisher.homePageUrl ? 'noopener noreferrer' : undefined}
				$clickable={!!publisher.homePageUrl}
			>
				<PublisherLogo src={publisher.logoImageUrl} />
				<RowSpacer size={15} />
				<PublisherName>{publisher.name}</PublisherName>
				{publisher.country && <PublisherCountry>{COUNTRY_LABEL[publisher.country] ?? publisher.country}</PublisherCountry>}
				{publisher.companyFoundingDate && <PublisherInfo>설립 {new Date(publisher.companyFoundingDate).getFullYear()}년</PublisherInfo>}
			</PublisherHeader>

			{parentPublisher && (
				<ParentPublisherContainer>
					<RelatedPublisherChip publisher={parentPublisher} />
					<ParentPublisherLabel>의 하위 출판사</ParentPublisherLabel>
				</ParentPublisherContainer>
			)}

			{childPublishers.length > 0 && (
				<>
					<RowSpacer size={10} />
					<SectionHeader>
						<SectionTitle>하위 출판사</SectionTitle>
						<SectionCount>{childPublishers.length.toLocaleString()}곳</SectionCount>
					</SectionHeader>
					<RowSpacer size={10} />
					<RelatedPublisherList>
						{childPublishers.map(child => (
							<RelatedPublisherChip key={child.urlName ?? child.name} publisher={child} />
						))}
					</RelatedPublisherList>
				</>
			)}

			<RowSpacer size={30} />

			<InfiniteScroll
				loader={<><RowSpacer /><SkeletonRow columns={columns || 7} /></>}
				next={paging.fetchNext}
				hasMore={paging.hasMore}
				dataLength={visibleBooks.length}
				className="overflow-hidden"
			>
				<SectionHeader>
					<SectionTitle>도서</SectionTitle>
					<SectionCount>{totalBookCount.toLocaleString()}권</SectionCount>
				</SectionHeader>
				<RowSpacer size={10} />
				<BookIsbnCardGrid ref={gridRef}>
					{visibleBooks.map(book => (
						<BookIsbnCard key={book.isbn13} book={book} />
					))}
				</BookIsbnCardGrid>
			</InfiniteScroll>

			<RowSpacer size={40} />
		</RouteContainer>
	)
}

const skeletonGlow = keyframes`
	0% { background-color: #e0e0e0; }
	40% { background-color: #d6d6d6; }
	60% { background-color: #c8c8c8; }
	100% { background-color: #e0e0e0; }
`

const SkeletonHeader = styled.div`
	text-align: center;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding-bottom: 20px;
`

const SkeletonBar = styled.div<{ width: number; height: number }>`
	width: ${props => props.width}px;
	height: ${props => props.height}px;
	background-color: #e0e0e0;
	border-radius: 4px;
	animation: ${skeletonGlow} 2s infinite;
`

const SkeletonCircle = styled.div`
	width: 100px;
	height: 100px;
	border-radius: 50%;
	background-color: #e0e0e0;
	margin: 0 auto;
	animation: ${skeletonGlow} 2s infinite;
`

const SkeletonBookCover = styled.div`
	height: 180px;
	background-color: #e0e0e0;
	border-radius: 6px;
	animation: ${skeletonGlow} 2s infinite;
`

const PublisherHeader = styled.div<{ $clickable?: boolean }>`
	text-align: center;
	text-decoration: none;
	color: inherit;
	display: block;
	padding-bottom: 20px;
	${props => props.$clickable && `cursor: pointer;`}
`

const LogoImage = styled.img<{ size: number }>`
	width: ${props => props.size}px;
	height: ${props => props.size}px;
	border-radius: 50%;
	object-fit: contain;
	background-color: var(--card-bg-color);
	border: 1px solid rgba(0, 0, 0, 0.06);
`

const LogoIconWrapper = styled.div<{ size: number }>`
	width: ${props => props.size}px;
	height: ${props => props.size}px;
	border-radius: 50%;
	background-color: var(--card-card-bg-color);
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto;
	color: var(--sub-text-color);
`

const PublisherName = styled.h1`
	font-size: 2rem;
	font-weight: bold;
	color: var(--text-color);
`

const PublisherCountry = styled.p`
	font-size: 14px;
	color: var(--sub-text-color);
	margin: 0;
`

const PublisherInfo = styled.p`
	font-size: 14px;
	color: var(--sub-text-color);
	margin: 0;
`

const ParentPublisherContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 6px;
`

const ParentPublisherLabel = styled.span`
	font-size: 14px;
	color: var(--sub-text-color);
`

const RelatedPublisherList = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
`

const relatedPublisherChipStyle = css`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 6px 14px 6px 6px;
	border-radius: 999px;
	border: 1px solid rgba(0, 0, 0, 0.08);
	background-color: var(--card-bg-color);
	color: var(--text-color);
	text-decoration: none;
	font-size: 14px;

	img, div {
		flex-shrink: 0;
		margin: 0;
	}
`

const RelatedPublisherChipStatic = styled.div`
	${relatedPublisherChipStyle}
`

const RelatedPublisherChipLink = styled(Link)`
	${relatedPublisherChipStyle}

	&:hover {
		color: var(--text-color);
		border-color: rgba(0, 0, 0, 0.2);
	}
`

const SectionHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding-top: 20px;
`

const SectionTitle = styled.h3`
	font-size: 1.2rem;
	font-weight: bold;
	color: var(--text-color);
	margin: 0;
`

const SectionCount = styled.span`
	font-size: 1rem;
	color: var(--sub-text-color);
`

export default BookPublisherRoute
